<script lang="ts">
  import OfflineIndicator from "./views/OfflineIndicator.svelte";
  import Firehose from "./views/Firehose.svelte";
  import Prototypes from "./views/Prototypes.svelte";
  import BottomNav from "./views/BottomNav.svelte";
  import ClubTab from "./views/ClubTab.svelte";
  import SwimHeroCard from "./views/SwimHeroCard.svelte";
  import SwimSwiper from "./views/SwimSwiper.svelte";
  import OpenWaterRouteCard from "./views/OpenWaterRouteCard.svelte";
  import StrokeSuspectsCard from "./lib/StrokeSuspectsCard.svelte";
  import SetRibbonSection from "./views/SetRibbonSection.svelte";
  import CalendarHeader from "./views/CalendarHeader.svelte";
  import SetsTab from "./views/SetsTab.svelte";
  import StrokesTab from "./views/StrokesTab.svelte";
  import ThisSwimSection from "./views/ThisSwimSection.svelte";
  import OverlayHost from "./views/OverlayHost.svelte";
  import MenuSheet from "./views/MenuSheet.svelte";
  import ScoresRingButton from "./views/ScoresRingButton.svelte";
  import SaveTroublePill from "./views/SaveTroublePill.svelte";
  import BetaSheet from "./lib/beta/BetaSheet.svelte";
  import { betaFixture, fromBridge, type BetaDiagnostics } from "./lib/beta/betaPanel";
  import { bridge } from "./bridge/client";
  import { nativeWebBuild, supportsMethod, supportsMethodStore } from "./lib/nativeMethods";
  import { BUILD_INFO } from "./lib/buildInfo";
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import { selectedWorkoutUUID } from "./lib/state";
  import { currentTab, showcase } from "./lib/nav";
  import { nativeTabsActive, shellTab } from "./lib/nativeTabs";
  import { goToTab } from "./lib/tabNav";
  import { shellSelectedTab, parseShellState } from "./lib/shellState";
  import { activeOverlay, openOverlay, refreshGoalPrefs } from "./lib/overlay";
  import { haptic } from "./lib/haptics";
  import { replayJournal, syncJournalCount, refreshLabelsFromNative } from "./lib/liveCorrection";
  import { replayGoalJournal, syncGoalJournalCount } from "./lib/goalCorrection";
  import { workouts, workoutsLoaded, loadWorkouts, refetchWorkouts } from "./lib/workoutsStore";
  import { offline } from "./lib/state";
  import { hasNativeShell } from "./bridge/client";
  import { initialZeroSwimState, nextZeroSwimAction } from "./lib/zeroSwimBoot";
  import { topBarSlot } from "./lib/topBar";
  import { devClubState } from "./lib/clubDevState";
  import { installOpenSetListener, receiveOpenSet } from "./lib/deepLink/openSet";

  // NATIVE TAB BAR (roadmap item 38). Under a shell that draws its own iOS tab
  // bar there is one web view PER TAB, and THIS view is pinned to one of them:
  // it renders that panel only, never the pill, and every cross-tab jump leaves
  // through `goToTab` (the bridge) instead of a store write that would land in
  // the wrong view. Read once — the shell's answer cannot change mid-session.
  const nativeTabs = nativeTabsActive();
  const pinnedTab = shellTab();
  // B-032: the web pill, the scrim under it and the bottom room the scrim needs all follow this
  // one flag. Under native tabs (the phone) the native bar's own edge effect is the scrim.
  let webPill = $derived(!$showcase && !nativeTabs);
  if (pinnedTab !== null) currentTab.set(pinnedTab);

  // Picking a swim anywhere (calendar sheet — openable from Swim's header or
  // Scores' "Pick a swim" — or the hero swiper/arrows) should drop the user
  // straight into that swim's stories on Swim. Initial null doesn't trigger.
  // Under native tabs the Swim panel is another web view, so the uuid rides
  // along on the bridge call and lands there as `swimset:select-swim`.
  $effect(() => {
    if ($selectedWorkoutUUID !== null) goToTab("swim", { workoutUUID: $selectedWorkoutUUID });
  });

  // Watchless launch-into-Club (Plan 5, D4 + audit F8). App.svelte owns the load
  // call it routes on (loadWorkouts is idempotent — CalendarHeader/ScoresTab's own
  // onMount calls become no-ops). Re-evaluates on every workouts-store change:
  // the boot resolution AND the swimset:auth-resolved refetch (Prototypes wires
  // handleAuthResolved → refetchWorkouts). currentTab is read via get() — NON-
  // reactively — so user navigation never re-triggers the decision.
  let zeroSwimState = initialZeroSwimState();
  $effect(() => {
    if ($showcase) return;
    const { action, state } = nextZeroSwimAction(zeroSwimState, {
      loaded: $workoutsLoaded,
      offline: $offline,
      workoutCount: $workouts.length,
      nativeShell: hasNativeShell(),
      // Under native tabs `currentTab` is this VIEW's identity and never
      // changes, so reading it here made the F8 auth-race overturn structurally
      // unreachable: a first-launch user who granted HealthKit after a
      // zero-swim boot stayed stranded on Club with their whole history one tab
      // away. The shell's selection is the only real answer.
      currentTab: nativeTabs ? ($shellSelectedTab ?? pinnedTab ?? "swim") : get(currentTab),
    });
    zeroSwimState = state;
    if (action === "route-club") goToTab("club");
    else if (action === "route-swim") goToTab("swim");
  });

  // Header controls. Both drive the single overlay slot; the sheets themselves
  // are mounted outside #app-shell (OverlayHost for Share, MenuSheet below).
  function openMenu() {
    haptic("light");
    openOverlay({ kind: "menu" });
  }
  function openShare() {
    haptic("light");
    openOverlay({ kind: "share" });
  }
  // B-031 (build 14): the Club gear moved up from the tab into the top bar's right slot. Same
  // native sheet, same gate as before (a shell that lists the method, or the browser dev seam).
  const supportsClubSettings = supportsMethodStore("openClubSettings");
  const clubDevSeam = devClubState() !== null;
  let rightSlot = $derived(topBarSlot($currentTab, $supportsClubSettings || clubDevSeam));
  function openClubSettings() {
    haptic("light");
    void bridge.openClubSettings();
  }
  // Beta pill (lane beta-panel): renders when the shell lists `betaDiagnostics`
  // or a fixture is named, so a browser or an older shell never shows made-up
  // diagnostics. The sheet re-reads the shell on every open (a tester fixes a
  // red row in Settings, comes back, and taps Beta again).
  const betaFixtureData = betaFixture();
  const supportsBeta = supportsMethodStore("betaDiagnostics");
  const betaAvailable = $derived(betaFixtureData !== null || $supportsBeta);
  let betaDiagnostics = $state<BetaDiagnostics | null>(betaFixtureData);
  let betaFailed = $state(false);
  async function loadBeta() {
    if (betaFixtureData) return;
    betaFailed = false;
    try {
      const r = await bridge.betaDiagnostics();
      betaDiagnostics = fromBridge(r, BUILD_INFO.commitSha, get(nativeWebBuild)?.isLatest ?? null);
    } catch {
      // Keep an earlier answer if there is one; otherwise the sheet says it couldn't check.
      betaFailed = betaDiagnostics === null;
    }
  }
  // Back from iOS Settings (the iCloud row's fix) with the sheet still open:
  // re-read, so a row the tester just fixed turns green without reopening.
  $effect(() => {
    if ($activeOverlay?.kind !== "beta") return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void loadBeta();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  });
  // DEBUG shells list `openDebugTools`: a long-press on the pill opens the
  // developer sheet (it replaced the bottom-left ladybug). Release never lists it.
  let betaHold: ReturnType<typeof setTimeout> | undefined;
  let betaHeld = false;
  function betaPressStart() {
    betaHeld = false;
    if (!supportsMethod("openDebugTools")) return;
    betaHold = setTimeout(() => {
      betaHeld = true;
      haptic("medium");
      void bridge.openDebugTools();
    }, 600);
  }
  function betaPressEnd() {
    clearTimeout(betaHold);
  }
  function openBeta() {
    if (betaHeld) {
      betaHeld = false;
      return;
    }
    haptic("light");
    void loadBeta();
    openOverlay({ kind: "beta" });
  }

  // Durable corrections: on boot, reflect any stranded journal entries in the
  // pill, then replay them; also replay when the first-launch auth sheet resolves
  // (the bridge may not have been ready at boot). Skipped on the public showcase.
  onMount(() => {
    if ($showcase) return;
    // Plan 5: the zero-swim boot route reads this store; idempotent everywhere else.
    void loadWorkouts();
    // The top-bar scores ring (every tab) reads the saved weekly goal.
    void refreshGoalPrefs();
    syncJournalCount();
    syncGoalJournalCount();
    void replayJournal();
    void replayGoalJournal();
    const onAuthResolved = () => {
      void replayJournal();
      void replayGoalJournal();
      // Final-review F8 regression: zero-swim boot routes to Club, which
      // unmounts the Swim panel and Prototypes.svelte's OWN auth-resolved →
      // refetchWorkouts listener with it. This listener must outlive tab
      // routing — Prototypes' copy dies when the Swim panel unmounts — so
      // App.svelte, the always-mounted owner of the routing decision, must
      // also own the refetch. Without it, the native one-per-launch nudge
      // finds no listener, workoutsStore's _fetchStarted guard stays latched
      // empty, and the zeroSwimBoot overturn (route back to Swim) can never
      // fire. refetchWorkouts() is idempotent/harmless if Prototypes' own
      // listener already re-fetched (covered by workoutsStore.test.ts).
      void refetchWorkouts();
    };
    window.addEventListener("swimset:auth-resolved", onAuthResolved);
    // Post-accept landing (spec 2026-09-01 §2b): native dispatches this right
    // after a CloudKit share accept succeeds (WebViewController.routeToClub),
    // then `swimset:club-status-refresh`, so the new member lands IN the Club
    // tab with a fresh roster instead of whatever tab happened to be showing.
    // Kept for an older shell (and for the non-native-tab path): the shell
    // dispatches this after a club share accept. Under native tabs the shell
    // ALSO selects the Club tab itself, and this view's copy is then a no-op
    // (goToTab returns early when it is already the pinned tab).
    const onRouteClub = () => goToTab("club");
    window.addEventListener("swimset:route-club", onRouteClub);
    // Native tabs: the shell is telling every view what it now has selected —
    // which tab, and which swim. Both halves matter: the tab is the only true
    // answer to "where is the user" once each view is pinned, and the swim is
    // what stops Scores from showing the swim you moved off on Swim.
    const onShellState = (e: Event) => {
      const state = parseShellState((e as CustomEvent).detail);
      if (!state) return;
      shellSelectedTab.set(state.tab);
      // Last-state marker for the DEBUG capture harness to assert on. Cheap, and
      // it means the cross-tab hand-off is checked against the running app
      // rather than against the code that is supposed to implement it.
      (window as unknown as Record<string, unknown>).__swimsetLastShellState =
        `${state.tab}:${state.workoutUUID ?? "none"}`;
      if (state.workoutUUID !== null && get(selectedWorkoutUUID) !== state.workoutUUID) {
        selectedWorkoutUUID.set(state.workoutUUID);
      }
    };
    window.addEventListener("swimset:shell-state", onShellState);
    // Native tabs: this tab just became visible again. Another tab's web view is
    // a separate JS heap, so anything it wrote since we last looked has to be
    // re-read — the corrections the owner just made on Swim are the case that
    // matters (crossTabState.test.ts). UserDefaults is the shared truth.
    const onTabAppeared = () => {
      // NOT refetchWorkouts(): its failure path replaces the real list with
      // STUB_WORKOUTS, and running it on every tab appearance turns one
      // transient bridge timeout in a locker room into a fabricated 1825 m
      // "swim" in the calendar. A genuinely new swim still arrives the way it
      // always did — next launch, or the auth-resolved nudge. What has to be
      // re-read here is what ANOTHER HEAP can have written since: the labels.
      void refreshLabelsFromNative();
      syncJournalCount();
      syncGoalJournalCount();
      // A goal saved in another tab's view is only in UserDefaults for us.
      void refreshGoalPrefs();
    };
    window.addEventListener("swimset:tab-appeared", onTabAppeared);
    // A friend's set link (deep-link N2): the native sheet's button dispatches
    // `swimset:open-set` into the Scores view. Here, not in ScoresTab, so a
    // single web view sitting on Swim still hears it and switches tabs.
    const removeOpenSet = installOpenSetListener(receiveOpenSet);
    // Second-chance replay (2026-07-09, wire-capture diagnosis): the boot replay
    // was the app's ONLY automatic retry, and it could die inside a busy launch
    // window (native boot work deafened the bridge and every call timed out) —
    // stranding an entry, and the pill, for the whole session even though later
    // saves worked. One delayed retry after the launch chaos settles closes that
    // shape. Journal-driven: a no-op when nothing is stranded.
    const secondChance = setTimeout(() => {
      void replayJournal();
      void replayGoalJournal();
    }, 20_000);
    return () => {
      clearTimeout(secondChance);
      window.removeEventListener("swimset:auth-resolved", onAuthResolved);
      window.removeEventListener("swimset:route-club", onRouteClub);
      window.removeEventListener("swimset:shell-state", onShellState);
      window.removeEventListener("swimset:tab-appeared", onTabAppeared);
      removeOpenSet();
    };
  });
</script>

<!--
  #app-shell wraps the page (header row included) and the nav so a single
  reactive `inert` makes the whole interactive surface unresponsive while any
  overlay is open. The sheets live in <OverlayHost /> and <MenuSheet />,
  siblings OUTSIDE this subtree, so they stay interactive. inert={false} would
  stringify on a non-boolean attr; gate to true|undefined so it renders ABSENT
  when no overlay is active.

  CRITICAL: #app-shell must stay a plain wrapper — NO position / transform /
  filter / z-index / opacity / will-change. Any of those creates a containing
  block / stacking context and breaks the position:fixed BottomNav and the
  env(safe-area-inset-*) math.
-->
<div id="app-shell" inert={$activeOverlay !== null ? true : undefined}>
  <!-- No native-tabs class: the iOS 26 floating tab bar sits where the web pill
       did and at close to the same height, so the bottom reservation the base
       `main` rule already makes still clears it (verified against the stills in
       docs/screenshots/2026-09-09-native-nav/), and the top strip is still the
       page's own because the shell draws no navigation bar. Nothing to restyle. -->
  <main id="app-root" class:showcase={$showcase} class:web-pill={webPill}>
    <!-- The Oura row (spec 2026-09-09 §1e, Scott ruling 6): Menu left, wordmark
         center, Share right. It stays under the native tab bar too: a native
         large title is not achievable without giving up the full-bleed backdrop
         (docs/lanes/2026-09-09-native-nav.md), so the shell draws no title of
         its own and this row is the top of every tab's web view. The public
         showcase keeps only the wordmark: a plain browser has no bridge for the
         menu rows and no swim of its own to share. -->
    <header class="top-row" class:showcase={$showcase}>
      {#if !$showcase}
        <!-- Left cell: Menu, then the Beta pill (Scott 2026-09-12: the bug icon
             moves up here, by the hamburger, in words a tester understands). -->
        <div class="top-left">
          <button
            type="button"
            class="top-btn"
            aria-label="Menu"
            aria-expanded={$activeOverlay?.kind === "menu"}
            onclick={openMenu}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16"/>
            </svg>
          </button>
          {#if betaAvailable}
            <button
              type="button"
              class="beta-btn"
              aria-label="Beta: build info, feedback, and setup check"
              aria-expanded={$activeOverlay?.kind === "beta"}
              onclick={openBeta}
              onpointerdown={betaPressStart}
              onpointerup={betaPressEnd}
              onpointercancel={betaPressEnd}
              onpointerleave={betaPressEnd}
              oncontextmenu={(e) => e.preventDefault()}
            >
              <span class="beta-pill">Beta</span>
            </button>
          {/if}
        </div>
      {/if}
      <h1>Swim Set Club</h1>
      {#if !$showcase}
        <!-- Two cells top right on every tab (B-031, build 14): the slot, then the
             scores ring. The slot holds Share where there is something to share
             (Swim: this swim, Sets: this set), the Club gear on Club (Scott
             2026-09-14, "keep the Club gear"), and an empty cell of the same
             width on Strokes, so the ring and the menu never move between tabs.
             The left cell is as wide as this one, so the wordmark stays centered. -->
        <div class="top-right">
          {#if rightSlot === "gear"}
          <button type="button" class="top-btn" aria-label="Club settings" onclick={openClubSettings}>
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          {:else if rightSlot === "empty"}
          <span class="top-slot" data-testid="top-slot-empty" aria-hidden="true"></span>
          {:else}
          <button
            type="button"
            class="top-btn"
            aria-label="Share"
            aria-expanded={$activeOverlay?.kind === "share"}
            onclick={openShare}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
          </button>
          {/if}
          <ScoresRingButton />
        </div>
      {/if}
    </header>

    {#if $showcase}
      <!-- PUBLIC showcase page. The chrome (banner + footer) is the ONLY new
           markup; everything between is the REUSED app output — the same
           SwimHeroCard and SetRibbonSection the app mounts, rendering the
           published swim through the real deriveWorkout pipeline. No ribbon,
           chip, color, or set-label code is duplicated here. MetricRings,
           BottomNav, the share FAB, the raw Firehose browser, and the offline
           badge are omitted because they need bridge.listWorkouts() (week
           distance, streak, weeks-swum) which a plain browser has no bridge to
           call — not for any HR reason. -->
      <section class="showcase-banner" aria-label="About Swim Set Club">
        <p class="kicker">iPhone app coming soon</p>
        <!-- Item 5: truthful copy that matches what the page shows (a set list),
             not an over-claimed full timeline (rings are hidden by design —
             they need the native bridge a browser lacks). -->
        <p class="lede">
          See how a swim breaks down, set by set.
        </p>
        <!-- Identity signal ABOVE the swim — first person, but still names Scott
             so a stranger learns whose swim this is. Handle + site are links. -->
        <p class="byline">
          My swim · by Scott · <a href="https://x.com/scottd3v" rel="noopener">@scottd3v</a>
          · <a href="https://alliteratelabs.com" rel="noopener">alliteratelabs.com</a>
        </p>
      </section>

      <div id="panel-swim" role="tabpanel" aria-label="Swim">
        <SwimHeroCard />
        <!-- showcase: the owner-personal first-time recognition callout is hidden;
             the signature sets are told through the ribbon labels themselves. -->
        <SetRibbonSection showcase={true} />
        <!-- The data engine runs; its owner-facing review deck is suppressed. -->
        <Prototypes showcase={true} />
      </div>

      <footer class="showcase-footer">
        <p class="follow">Follow along</p>
        <!-- Item 3: the destinations are explicit, labeled, and tappable. -->
        <nav class="links" aria-label="Follow Swim Set Club">
          <a href="https://x.com/scottd3v" rel="noopener">@scottd3v on X</a>
          <a href="https://alliteratelabs.com" rel="noopener">alliteratelabs.com</a>
        </nav>
        <!-- MHMDA (RCW 19.373.020): the health data policy is linked from this home too; labels match the landing footer.
             swimsetclub.com, absolute: /showcase also renders in the app's web view (swimset.club = no way back). -->
        <p class="policies"><a href="https://swimsetclub.com/privacy" rel="noopener">Privacy</a> · <a href="https://swimsetclub.com/health-privacy" rel="noopener">Health data</a></p>
      </footer>
    {:else if $currentTab === "swim"}
      <div id="panel-swim" role="tabpanel" aria-label="Swim">
        <CalendarHeader />
        <SwimSwiper />
        <StrokeSuspectsCard />
        <SetRibbonSection />
        <OpenWaterRouteCard />
        <Prototypes />
        <!-- The per-swim charts, with the swim (Q1, 2026-09-10); moved from Sets. -->
        <ThisSwimSection />
        <Firehose />
      </div>
    {:else if $currentTab === "sets"}
      <div id="panel-sets" role="tabpanel" aria-label="Sets">
        <!-- Prototypes is the firehose->derive->publish ENGINE: it fills
             currentDerivedWorkout. Sets' own cards read caches only (the
             per-swim charts moved to Swim, Q1). Under native tabs this is a
             SEPARATE web view that never mounts Swim, and the top bar's Share
             here shares the loaded swim's set, so it keeps a headless copy. -->
        {#if nativeTabs}
          <Prototypes headless={true} />
        {/if}
        <SetsTab />
      </div>
    {:else if $currentTab === "strokes"}
      <div id="panel-strokes" role="tabpanel" aria-label="Strokes">
        <!-- No headless engine: Strokes' cards read caches only, and it has no
             Share control, so nothing here needs a loaded swim. -->
        <StrokesTab />
      </div>
    {:else}
      <div id="panel-club" role="tabpanel" aria-label="Club">
        <ClubTab />
      </div>
    {/if}

    {#if !$showcase}
      <OfflineIndicator />
    {/if}
  </main>

  <!-- B-032 (build 14): text scrolling under the web pill fades instead of being sliced mid-word,
       on every tab. Only where the web pill is drawn: under native tabs the native bar's own edge
       effect is the scrim, and a second one stacked under it with a dead gap (verifier 2026-09-14).
       main.web-pill's bottom padding keeps the last line above the scrim's top at max scroll. -->
  {#if webPill}
    <div class="tabbar-scrim" data-testid="tabbar-scrim" aria-hidden="true"></div>
  {/if}

  <!-- The pill is the page's own nav. It goes when — and only when — a shell
       is drawing the native tab bar instead; a browser and any older shell
       still get it. The showcase never had one. -->
  {#if webPill}
    <BottomNav />
  {/if}
</div>

<OverlayHost />
<MenuSheet open={$activeOverlay?.kind === "menu"} />
{#if betaAvailable && !$showcase}
  <BetaSheet open={$activeOverlay?.kind === "beta"} diagnostics={betaDiagnostics} failed={betaFailed} onRefresh={loadBeta} />
{/if}

{#if !$showcase}
  <SaveTroublePill />
{/if}

<style>
  main {
    padding: var(--space-4);
    /* Clear the dynamic island / status bar — without this the wordmark
       and hero sit under the island on real device. */
    padding-top: calc(var(--space-4) + env(safe-area-inset-top, 0px));
    /* Reserve room for the floating nav pill so content never hides under it.
       Clears the pill (~60px) + its bottom offset (var(--space-3)) + the device
       home-indicator inset via env(). The env() term resolves to 0px on devices
       with no inset, so this is correct on both home-indicator and no-inset
       phones — unlike a flat value, which tucks the last rows under the pill on
       home-indicator iPhones. */
    padding-bottom: calc(72px + var(--space-3) + env(safe-area-inset-bottom, 0px));
    max-width: 720px;
    margin: 0 auto;
  }
  /* B-032: with the web pill, plus the scrim's 36 px fade and an 8 px gap, so at max scroll the
     last line sits above the scrim (64 px pill + 12 px offset + 36 px fade = 112 px tall).
     Native tabs keep the base room: no web scrim there. */
  main.web-pill {
    padding-bottom: calc(72px + var(--space-3) + 44px + env(safe-area-inset-bottom, 0px));
  }
  .tabbar-scrim {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(112px + env(safe-area-inset-bottom, 0px));
    pointer-events: none;
    /* Under the dock (30), over the page and its sticky date row (5). */
    z-index: 25;
    background: linear-gradient(
      to bottom,
      color-mix(in srgb, var(--bg-canvas) 0%, transparent) 0,
      color-mix(in srgb, var(--bg-canvas) 85%, transparent) 36px,
      color-mix(in srgb, var(--bg-canvas) 95%, transparent) 100%
    );
  }
  /* The Oura row: three cells, the wordmark dead center regardless of the
     controls' widths (a 3-column grid, not space-between, so the mark does not
     drift when one side is empty on the showcase). */
  /* 96px side cells: the right one holds share + ring (44 each), the left one
     Menu (44) + the Beta pill (~50). Both stay equal so the wordmark is centered. */
  header.top-row {
    display: grid;
    grid-template-columns: 96px 1fr 96px;
    align-items: center;
    padding: 0 0 var(--space-3);
  }
  header.top-row.showcase {
    grid-template-columns: 1fr;
  }
  header.top-row h1 {
    grid-column: 2;
    text-align: center;
    font: var(--type-title-subtitle);
    color: var(--text-primary);
    letter-spacing: 0.04em;
    /* No text-transform: the wordmark is title-case "Swim Set Club" (DESIGN.md,
       "Name and wordmark"). A lowercase transform used to render it as the old
       one-word mark no matter what the markup said. */
  }
  header.top-row.showcase h1 {
    grid-column: 1;
  }
  .top-btn {
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--text-primary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background-color var(--dur-micro) var(--ease-swim),
                transform var(--dur-micro) var(--ease-swim);
  }
  .top-left {
    grid-column: 1;
    grid-row: 1;
    display: flex;
    align-items: center;
  }
  /* 44pt tap target; the visible pill inside it is smaller. */
  .beta-btn {
    height: 44px;
    padding: 0 2px;
    display: inline-flex;
    align-items: center;
    border: 0;
    background: transparent;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: transform var(--dur-micro) var(--ease-swim);
  }
  .beta-btn:active {
    transform: scale(0.94);
  }
  .beta-btn:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
    border-radius: var(--radius-pill);
  }
  .beta-pill {
    display: inline-flex;
    align-items: center;
    height: 26px;
    padding: 0 9px;
    border: 1px solid var(--divider-strong);
    border-radius: var(--radius-pill);
    background: var(--bg-raised);
    color: var(--accent-primary);
    font: 600 13px/1 var(--font-text);
    letter-spacing: 0.02em;
  }
  .top-right {
    grid-column: 3;
    display: flex;
    justify-content: flex-end;
  }
  .top-slot {
    flex: 0 0 44px;
    height: 44px;
  }
  .top-btn:hover {
    background: var(--bg-raised);
  }
  .top-btn:active {
    transform: scale(0.94);
  }
  .top-btn:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }

  /* Calm column: the hero and the set list are separated sections, not cards
     jammed together. One consistent gap does the breathing room (feel pass). */
  #panel-swim {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
  }


  /* Showcase (public page): no BottomNav pill, so reclaim the bottom padding
     the normal layout reserves for it. The banner + footer chrome below wrap
     the reused swim components. */
  main.showcase {
    padding-bottom: calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
  }

  .showcase-banner {
    margin: 0 0 var(--space-4);
    padding: var(--space-3) var(--space-4);
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
  }
  .showcase-banner .kicker {
    margin: 0;
    font: var(--type-caption);
    color: var(--accent-primary);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .showcase-banner .lede {
    /* The public landing's lead line — this is the page's one sentence, so it
       reads at a headline weight (26px/600), a clear jump above the kicker label
       and byline. Leads the eye before the swim content loads below, and stays a
       step under the swim's own 76px hero number (One-Hero rule preserved).
       Canonical scale token — no parallel scale. */
    margin: var(--space-2) 0 0;
    font: var(--type-lede);
    letter-spacing: -0.01em;
    color: var(--text-primary);
  }
  .showcase-banner .byline {
    margin: var(--space-2) 0 0;
    font-size: 15px;
    line-height: 1.4;
    color: var(--text-muted);
  }
  /* Item 3: without an explicit rule these links fell back to the browser
     default dark blue (near-invisible on the near-black card for a low-vision
     reader). Force the bright accent + underline so the handle and site read
     clearly. Same treatment shared with the footer links below. */
  .showcase-banner .byline a,
  .showcase-footer .links a,
  .showcase-footer .policies a {
    color: var(--accent-secondary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .showcase-footer {
    margin-top: var(--space-5);
    padding-top: var(--space-4);
    border-top: 1px solid var(--divider);
    text-align: center;
  }
  .showcase-footer .follow {
    margin: 0 0 var(--space-2);
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .showcase-footer .links {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: center;
  }
  .showcase-footer .policies {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--text-muted);
  }
  .showcase-footer .policies a {
    display: inline-flex;
    align-items: center;
    min-height: 44px; /* the brief's 44pt target */
  }
  .showcase-footer .links a {
    /* color + underline come from the shared .byline a / .links a rule above
       (Item 3 contrast fix); keep only the footer-specific size + tap padding. */
    font-size: 16px;
    padding: var(--space-2) var(--space-3);
  }
</style>
